import React, { useCallback, useRef, useState } from 'react';
import { Button, Popup, PopupSection, PopupActionList } from '@ui';
import { translate } from '@utils/i18n';
import './AiConfigureMenuStyles.scss';
const NAMESPACE = 'features.ai_configure_menu';
/**
 * AiConfigureMenu
 *
 * Reusable admin-only "Configure" control shared by the Inspection Assistant
 * Review screen (IP-14857) and the IP Assistant chat widget header (IP-14858).
 * Renders a QUI Button (leading `fa-sliders`) that opens a QUI Popup listing
 * the AI configuration destinations. Navigation is a full-page redirect so it
 * works from both a React island (review tab) and the chat widget overlay.
 *
 * @version 1.0.0
 * @since 2026-07-20
 */
export function AiConfigureMenu({ items, visible = false, variant = 'button', mode = 'light', className = '' }) {
    const anchorRef = useRef(null);
    const [open, setOpen] = useState(false);
    const handleToggle = useCallback(() => setOpen((prev) => !prev), []);
    const handleClose = useCallback(() => setOpen(false), []);
    // Fail-closed: render nothing unless the admin gate is strictly true.
    if (visible !== true || items.length === 0)
        return null;
    const configureLabel = translate('configure', { namespace: NAMESPACE });
    const isIcon = variant === 'icon';
    const triggerClass = ['ai-configure-menu__trigger', isIcon ? 'ai-configure-menu__trigger--icon' : '', className]
        .filter(Boolean)
        .join(' ');
    return (React.createElement("div", { className: "ai-configure-menu" },
        React.createElement(Button, { ref: anchorRef, mode: mode, variant: "default", className: triggerClass, onClick: handleToggle, "aria-haspopup": "menu", "aria-expanded": open, "aria-label": configureLabel, title: configureLabel, "data-testid": "ai-configure-menu-trigger" },
            React.createElement("i", { className: "fa-light fa-sliders", "aria-hidden": "true" }),
            !isIcon && React.createElement("span", { className: "ai-configure-menu__trigger-label" }, configureLabel)),
        React.createElement(Popup, { anchor: anchorRef.current, show: open, onClose: handleClose, position: "bottom-right", containerClass: "ai-configure-menu__popup" },
            React.createElement(PopupSection, null,
                React.createElement(PopupActionList, null, items.map((item) => (
                // Full-page navigation to the settings route, so an anchor (keeps
                // middle-click / open-in-new-tab). Styled as a Quimby popup action
                // row via `qmb-ui-popup__action` + tokenized `ai-configure-menu__item`
                // (no anchor blue/underline) — matches the prototype markup.
                React.createElement("a", { key: item.key, href: item.href, role: "menuitem", className: "qmb-ui-popup__action ai-configure-menu__item", "data-testid": `ai-configure-menu-item-${item.key}`, onClick: handleClose },
                    React.createElement("i", { className: "fa-light fa-gear ai-configure-menu__item-icon", "aria-hidden": "true" }),
                    React.createElement("span", { className: "ai-configure-menu__item-label" }, item.label)))))))));
}
AiConfigureMenu.displayName = 'AiConfigureMenu';
